import { useEffect, useState } from "react";

import { putReservationState } from "@/apis/reservation";
import Button from "@/components/Button";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import ReservationModal from "@/components/Modal/ReservationModal";
import Table, { type SortState, type SortDirection } from "@/components/Table";
import {
  APPROVE_RESERVATION_MESSAGE,
  CONFIRM_APPROVE_RESERVATION_MESSAGE,
  CONFIRM_REJECT_RESERVATION_MESSAGE,
  EMPTY_RESERVATION_LIST_MESSAGE,
  REJECT_RESERVATION_MESSAGE,
} from "@/constants/messages/admin";
import { REQUEST_ERROR_MESSAGE } from "@/constants/messages/common";
import useModal from "@/hooks/useModal";
import ReservationDetails from "@/pages/Admin/components/ReservationInfo/ReservationDetails";
import type {
  ReservationByState,
  ReservationStateCode,
} from "@/types/reservation";
import type { CellInfo } from "@/types/table";
import {
  getReservationBodyData,
  getTableBody,
  getTableHeader,
} from "@/utils/table";

const reservationTableHeader = [
  "날짜",
  "시간",
  "장소",
  "공동체",
  "예약자",
  "상태",
  "사용 목적",
  "",
];
// 컬럼 폭 고정 (페이지 변경 시 폭이 변하지 않도록)
const reservationTableWidths = [
  "11%", // 날짜
  "12%", // 시간
  "16%", // 장소
  "11%", // 공동체
  "10%", // 예약자
  "9%", // 상태
  "17%", // 사용 목적
  "14%", // actions
];

const header = getTableHeader(reservationTableHeader, reservationTableWidths);

interface ReservationTableProps {
  reservations: ReservationByState[];
  updateReservations: () => void;
  sortState: SortState;
  onSort: (column: string, direction: SortDirection) => void;
}
const SORTABLE_COLUMNS = ["날짜", "장소", "예약자"];

const ReservationTable = ({
  reservations,
  updateReservations,
  sortState,
  onSort,
}: ReservationTableProps) => {
  const [reservationBody, setReservationBody] = useState<CellInfo[][]>([]);
  const [selectedReservation, setSelectedReservation] =
    useState<ReservationByState | null>(null);
  const [alertMessage, setAlertMessage] = useState("");

  const reservationModal = useModal();
  const confirmApproveModal = useModal();
  const confirmRejectModal = useModal();
  const confirmDeleteModal = useModal();
  const alertModal = useModal();

  const [actionLoading, setActionLoading] = useState(false);

  const changeReservationState = async (
    rsvtId: number,
    sttCd: ReservationStateCode,
  ) => {
    await putReservationState({ rsvtId, sttCd });
    updateReservations();
  };

  const handleConfirmApprove = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedReservation) {
        setAlertMessage(REQUEST_ERROR_MESSAGE);
      } else {
        await changeReservationState(selectedReservation?.rsvtId, "approve");
        setAlertMessage(APPROVE_RESERVATION_MESSAGE);
      }
    } catch {
      setAlertMessage(REQUEST_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmApproveModal.onClose();
  };

  const handleConfirmReject = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedReservation) {
        setAlertMessage(REQUEST_ERROR_MESSAGE);
      } else {
        await changeReservationState(selectedReservation?.rsvtId, "reject");
        setAlertMessage(REJECT_RESERVATION_MESSAGE);
      }
    } catch {
      setAlertMessage(REQUEST_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmRejectModal.onClose();
  };

  const handleConfirmDelete = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (!selectedReservation) {
        setAlertMessage(REQUEST_ERROR_MESSAGE);
      } else {
        await changeReservationState(selectedReservation?.rsvtId, "deleted");
        setAlertMessage("예약이 삭제되었습니다.");
      }
    } catch {
      setAlertMessage(REQUEST_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmDeleteModal.onClose();
  };

  useEffect(() => {
    const reservationTableBody = reservations.map(reservation => {
      const { sttCd } = reservation;

      const showReservationModal = () => {
        setSelectedReservation(reservation);
        reservationModal.onOpen();
      };

      const approveReservation = () => {
        setSelectedReservation(reservation);
        confirmApproveModal.onOpen();
      };

      const rejectReservation = () => {
        setSelectedReservation(reservation);
        confirmRejectModal.onOpen();
      };

      const deleteReservation = () => {
        setSelectedReservation(reservation);
        confirmDeleteModal.onOpen();
      };

      // For reject/cancel/deleted states, use deleteReservation for the trash icon
      const rejectOrDeleteMethod =
        sttCd === "reject" || sttCd === "cancel" ? deleteReservation : rejectReservation;

      return getReservationBodyData(
        reservation,
        sttCd,
        showReservationModal,
        approveReservation,
        rejectOrDeleteMethod,
      );
    });

    setReservationBody(
      getTableBody(reservationTableHeader, reservationTableBody),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations]);

  if (reservations.length === 0 && !alertModal.isOpen) {
    return (
      <div className="flex h-20 items-center justify-center">
        <div>{EMPTY_RESERVATION_LIST_MESSAGE}</div>
      </div>
    );
  }

  return (
    <>
      <Table
        header={header}
        body={reservationBody}
        sortableColumns={SORTABLE_COLUMNS}
        sortState={sortState}
        onSort={onSort}
      />
      {reservationModal.isOpen && (
        <ReservationModal onClose={reservationModal.onClose}>
          <ReservationModal.Header>예약 상세 정보</ReservationModal.Header>
          <ReservationModal.Content>
            {selectedReservation && (
              <ReservationDetails reservation={selectedReservation} />
            )}
          </ReservationModal.Content>
          <ReservationModal.Footer>
            <Button variant="primary" onClick={reservationModal.onClose}>
              확인
            </Button>
          </ReservationModal.Footer>
        </ReservationModal>
      )}
      {confirmApproveModal.isOpen && (
        <ConfirmModal
          title="예약 승인"
          onConfirm={handleConfirmApprove}
          onClose={confirmApproveModal.onClose}
        >
          {CONFIRM_APPROVE_RESERVATION_MESSAGE}
        </ConfirmModal>
      )}
      {confirmRejectModal.isOpen && (
        <ConfirmModal
          title="예약 반려"
          onConfirm={handleConfirmReject}
          onClose={confirmRejectModal.onClose}
        >
          {CONFIRM_REJECT_RESERVATION_MESSAGE}
        </ConfirmModal>
      )}
      {confirmDeleteModal.isOpen && (
        <ConfirmModal
          title="예약 삭제"
          onConfirm={handleConfirmDelete}
          onClose={confirmDeleteModal.onClose}
        >
          이 예약을 삭제하시겠습니까?
        </ConfirmModal>
      )}
      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>{alertMessage}</AlertModal>
      )}
    </>
  );
};

export default ReservationTable;
